import { useMemo, useState, type FormEvent } from 'react'
import type { Client } from '../types'
import { ClientLogoField } from './ClientLogoField'
import { Icon } from './Icon'

interface OperationDraft {
  key: string
  original?: string
  name: string
}

interface Props {
  client: Client
  onClose: () => void
  onSave: (client: Client, operationChanges: Record<string, string | undefined>) => void
}

export function ClientManagementModal({ client, onClose, onSave }: Props) {
  const [name, setName] = useState(client.name)
  const [logo, setLogo] = useState(client.logo)
  const [logoProcessing, setLogoProcessing] = useState(false)
  const [operations, setOperations] = useState<OperationDraft[]>(() => client.operations.map(operation => ({ key: crypto.randomUUID(), original: operation, name: operation })))
  const [error, setError] = useState('')
  const normalizedOperations = useMemo(() => operations.map(operation => operation.name.trim()).filter(Boolean), [operations])

  function addOperation() {
    setOperations(current => [...current, { key: crypto.randomUUID(), name: '' }])
    setError('')
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const normalizedKeys = normalizedOperations.map(operation => operation.toLocaleLowerCase('pt-BR'))
    if (new Set(normalizedKeys).size !== normalizedKeys.length) {
      setError('Não é possível cadastrar operações com nomes repetidos.')
      return
    }
    const operationChanges = Object.fromEntries(client.operations.map(original => {
      const draft = operations.find(operation => operation.original === original)
      return [original, draft?.name.trim() || undefined]
    }))
    onSave({ ...client, name: name.trim(), logo, operations: normalizedOperations }, operationChanges)
  }

  return <div className="modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <form className="modal client-management-modal" onSubmit={submit}>
      <div className="modal-head"><div><span className="eyebrow">CLIENTE E OPERAÇÕES</span><h3>Editar {client.name}</h3></div><button type="button" onClick={onClose} aria-label="Fechar">×</button></div>
      <div className="field"><label htmlFor="managed-client-name">NOME DO CLIENTE</label><input id="managed-client-name" value={name} onChange={event => setName(event.target.value)} required /></div>
      <ClientLogoField clientName={name} value={logo} onChange={setLogo} onProcessingChange={setLogoProcessing}/>
      <div className="operations-editor-head"><div><b>Operações</b><small>Renomeie, adicione ou exclua unidades e hubs deste cliente.</small></div><button type="button" className="secondary compact-action" onClick={addOperation}><Icon name="plus"/>Adicionar operação</button></div>
      <div className="operations-editor">
        {operations.map((operation, index) => <div className="operation-editor-row" key={operation.key}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <input aria-label={`Nome da operação ${index + 1}`} value={operation.name} onChange={event => { const nextName = event.target.value; setOperations(current => current.map(item => item.key === operation.key ? { ...item, name: nextName } : item)); setError('') }} placeholder="Nome da operação" autoFocus={!operation.original}/>
          <button type="button" className="delete-operation" onClick={() => { setOperations(current => current.filter(item => item.key !== operation.key)); setError('') }} aria-label={`Excluir operação ${operation.name || index + 1}`} title="Excluir operação"><Icon name="trash"/></button>
        </div>)}
        {!operations.length && <div className="empty-operations">Nenhuma operação cadastrada. Você pode salvar o cliente assim ou adicionar uma operação.</div>}
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="modal-actions"><button type="button" className="secondary" onClick={onClose}>Cancelar</button><button className="primary" disabled={logoProcessing}>{logoProcessing ? 'Processando logo…' : 'Salvar alterações'}</button></div>
    </form>
  </div>
}
